import { List } from "immutable";
import _ from "lodash";
import createCachedSelector from "re-reselect";

import {
  IDENTIFIER_LIST_DOWN_ARROW_MARGIN_X,
  IDENTIFIER_LIST_DOWN_ARROW_SIZE,
  IDENTIFIER_SHRINK_MIN_WIDTH,
  OUTLINE_CELL_MIN_HEIGHT,
  OUTLINE_CELL_PADDING,
  OUTLINE_CELL_ROW_GUTTER_WIDTH,
  OUTLINE_DEFAULT_COLUMN_WIDTH,
  OUTLINE_HEADER_HEIGHT,
  OUTLINE_ID_COLUMN_WIDTH,
  OUTLINE_LABEL_COLUMN_WIDTH,
  OUTLINE_NARRATIVE_COLUMN_WIDTH,
  OUTLINE_SEQUENCE_COLUMN_WIDTH,
  OUTLINE_SUMMARY_COLUMN_WIDTH,
  TABLE_CELL_IDENTIFIERS_MAX_ROWS,
  TABLE_CELL_MAX_IDENTIFIERS,
  TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL,
  TABLE_CELL_TEXT_AREA_PADDING_VERTICAL,
  TABLE_IDENTIFIER_SLUG_MARGIN_X,
  TABLE_IDENTIFIER_SLUG_MIN_WIDTH
} from "../../constants";
import { DependencyTypes } from "../../constants/dependencyTypes";
import interfaceMessages from "../../constants/interfaceMessages";
import outlineColumnIds, { OUTLINE_COLUMN_BULK_CHANGE_DATES } from "../../constants/outlineColumnIds";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import TableCellTypes from "../../constants/tableCellTypes";
import TableRowTypes from "../../constants/tableRowTypes";
import {
  TAG_CELL_MARGIN_LEFT,
  TAG_CELL_MARGIN_TOP,
  TAG_CROSS_HEIGHT,
  TAG_CROSS_MARGIN_LEFT,
  TAG_CROSS_WIDTH,
  TAG_MARGIN_BOTTOM,
  TAG_MARGIN_RIGHT,
  TAG_PADDING_BOTTOM,
  TAG_PADDING_LEFT,
  TAG_PADDING_RIGHT,
  TAG_PADDING_TOP,
  TAGS_INPUT_PLACEHOLDER,
  TAGS_INPUT_WIDTH
} from "../../constants/tags";
import dialogs from "../../dialogs";
import { appStateSelectors } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { characterWidthMapSelectors } from "../../modules/characterWidthMap";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { isOngoingAllowedForCalendar } from "../../modules/definition/calendar/selectors";
import { colorSelectors } from "../../modules/definition/colors";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { dependencyOperations } from "../../modules/dependencies";
import displayCacheSelectors from "../../modules/display/selectors";
import { itemOperations, itemSelectors } from "../../modules/items";
import sortFunctions from "../../modules/items/sortFunctions";
import utils from "../../modules/items/utils";
import { relationshipOperations, relationshipSelectors } from "../../modules/relationships";
import { tagOperations, tagSelectors } from "../../modules/tags";
import { immutableRecords } from "../../types";
import { SizeRecord } from "../../types/immutableRecords/helper/SizeRecord";
import { calcSingleLineTextSize, calcTextSize } from "../../utils/calcTextSize";
import { wrapEditingComponent } from "../../utils/codeSplit";
import { listToMap } from "../../utils/immutableExtensions";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { tableCellTypeForProperty } from "../../utils/tableColumns";
import workViewSelectors from "../workView/selectors";

const getAvailableItemsForReference = (reference, allIdsForType, itemsById) =>
  reference.types.map(typeId => allIdsForType.get(typeId).map(objId => itemsById.get(objId))).flatten();

// Calculate all available columns for an SpreadsheetView, based on the
// current definitions.
/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {List} orderedTypes
 * @param {boolean} showSequenceNumber
 */
const getAllowedColumns = createCachedSelector(
  // work out our column type
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getPropertiesById(state),
  (state, viewId, orderedTypes) => orderedTypes,
  (state, viewId, orderedTypes, showSequenceNumber) => showSequenceNumber,
  state => isOngoingAllowedForCalendar(calendarSelectors.getCalendar(state)),
  // calculate result: (list of entities matching the type)
  (referencesById, propertiesById, visibleTypes, showSequenceNumber, isOngoingAllowed) => {
    let defaultColumns = List([
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.label,
        label: "Label",
        cellType: TableCellTypes.wrappedtext,
        width: OUTLINE_LABEL_COLUMN_WIDTH + OUTLINE_ID_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.identifier,
        label: "ID",
        cellType: TableCellTypes.identifier,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.type,
        label: "Type",
        cellType: TableCellTypes.dropdown,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH,
        canDelete: false
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.color,
        label: "Color",
        cellType: TableCellTypes.colorDropdown,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH,
        canDelete: false
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.start,
        label: "Start Date",
        cellType: TableCellTypes.date,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.latestStart,
        label: "Latest Start Date",
        cellType: TableCellTypes.date,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.duration,
        label: "Duration",
        cellType: TableCellTypes.duration,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.earliestEnd,
        label: "Earliest End Date",
        cellType: TableCellTypes.date,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.end,
        label: "End Date",
        cellType: TableCellTypes.date,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH,
        canDelete: false
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.childRange,
        label: "Child Range",
        cellType: TableCellTypes.text,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH,
        canDelete: false
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.blocks,
        label: "Blocks",
        cellType: TableCellTypes.multipleReference,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.blockedBy,
        label: "Blocked By",
        cellType: TableCellTypes.multipleReference,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.summary,
        label: "Summary",
        cellType: TableCellTypes.multitext,
        width: OUTLINE_SUMMARY_COLUMN_WIDTH
      }),
      immutableRecords.TableColumnRecord({
        id: outlineColumnIds.tag,
        label: "Tags",
        cellType: TableCellTypes.tag,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      })
    ]);

    if (showSequenceNumber) {
      defaultColumns = defaultColumns.insert(
        2,
        immutableRecords.TableColumnRecord({
          id: outlineColumnIds.sequence,
          label: "#",
          cellType: TableCellTypes.label,
          width: OUTLINE_SEQUENCE_COLUMN_WIDTH
        })
      );
    } else {
      defaultColumns = defaultColumns.insert(
        2,
        immutableRecords.TableColumnRecord({
          id: outlineColumnIds.narrative,
          label: "Position",
          cellType: TableCellTypes.label,
          width: OUTLINE_NARRATIVE_COLUMN_WIDTH
        })
      );
    }

    if (isOngoingAllowed) {
      defaultColumns = defaultColumns.insert(
        5,
        immutableRecords.TableColumnRecord({
          id: outlineColumnIds.ongoing,
          label: "Ongoing",
          cellType: TableCellTypes.checkbox,
          width: OUTLINE_DEFAULT_COLUMN_WIDTH
        })
      );
    }

    const visibleReferences = visibleTypes
      .toList()
      .map(t => t.references)
      .flatten()
      .toSet()
      .toList();
    const visibleProperties = visibleTypes
      .toList()
      .map(t => t.properties)
      .flatten()
      .toSet()
      .toList();

    const referenceColumns = visibleReferences.map(rId => {
      const ref = referencesById.get(rId);
      return immutableRecords.TableColumnRecord({
        id: ref.id,
        label: getLabelWithUntitled(ref.label),
        // options,
        cellType: TableCellTypes.multipleReference,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      });
    });

    const propertyColumns = visibleProperties.map(pId => {
      const prop = propertiesById.get(pId);
      const cellType = tableCellTypeForProperty(prop);
      return immutableRecords.TableColumnRecord({
        id: prop.id,
        label: getLabelWithUntitled(prop.label),
        cellType,
        width: OUTLINE_DEFAULT_COLUMN_WIDTH
      });
    });

    return defaultColumns.concat(referenceColumns).concat(propertyColumns);
  }
)(workViewSelectors.getViewId);

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {List} orderedTypes
 * @param {boolean} showSequenceNumber
 */
const getAvailableColumns = createCachedSelector(
  // work out our column type
  getAllowedColumns,
  // calculate result: (list of entities matching the type)
  availableColumns =>
    availableColumns
      .filter(
        c =>
          c.id !== outlineColumnIds.label && c.id !== outlineColumnIds.sequence && c.id !== outlineColumnIds.narrative
      )
      .map(c => immutableRecords.TableAvailableColumnRecord(c))
)(workViewSelectors.getViewId);

const getAllowedTypesForItem = (visibleTypes, strictFolderStructure, item) => {
  if (!strictFolderStructure || !item) {
    return visibleTypes;
  }
  const type = visibleTypes.find(t => t.id === item.type);
  return visibleTypes.filter(t => t.isNarrativeFolder === type.isNarrativeFolder);
};

// Provide a list of available items for a given column
/**
 *
 * @param {TimelineRecord} state
 * @param {List} orderedTypes
 * @param {string} columnId
 */
const getOptionsForColumn = createCachedSelector(
  // work out our column type
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getPropertiesById(state),
  // definitionSelectors.getColorMap,
  colorSelectors.getColors,
  tagSelectors.getTagsArray,
  state => narrativeSettingsSelectors.getNarrativeUsesStrictFolderStructure(state),
  state => state.data.items.byId,
  state => state.data.items.allIdsForType,
  (state, orderedTypes) => orderedTypes,
  (state, orderedTypes, columnId) => columnId,
  (state, orderedTypes, columnId, rowId) => rowId,
  // calculate result: (list of entities matching the type)
  (
    typesById,
    referencesById,
    propertiesById,
    orderedColors,
    tagsArray,
    strictFolderStructure,
    itemsById,
    allIdsForType,
    visibleTypes,
    columnId,
    rowId
  ) => {
    // Note: blocks and blockedBy handled separately as the available options
    // changes for each item.

    // Handle the easy, persistent cases
    switch (columnId) {
      case outlineColumnIds.type:
        return typeSelectors.getTypeOptionsFromTypes(
          getAllowedTypesForItem(visibleTypes, strictFolderStructure, itemsById.get(rowId))
        );
      case outlineColumnIds.color:
        return definitionSelectors.getColorOptionsFromColorMap(orderedColors);
      case outlineColumnIds.tag:
        return tagsArray;
    }

    // Now handle the references
    if (referencesById.has(columnId)) {
      const ref = referencesById.get(columnId);
      const availableItems = getAvailableItemsForReference(ref, allIdsForType, itemsById);
      let options = availableItems
        .map(obj => {
          return immutableRecords.DropdownOptionRecord({
            label: getLabelWithUntitled(obj.label),
            value: obj.id,
            displayId: obj.displayId
          });
        })
        .sort(sortFunctions.byLabel);
      return options;
    }

    if (propertiesById.has(columnId)) {
      const prop = propertiesById.get(columnId);
      if (prop.format === PropertyFormatIdentifier.list && prop.allowed) {
        return prop.allowed
          .map(opt => {
            return immutableRecords.DropdownOptionRecord({
              label: opt.label,
              value: opt.id
            });
          })
          .insert(0, immutableRecords.DropdownOptionRecord({ label: "None", value: null }));
      }
    }
  }
)((state, orderedTypes, columnId) => columnId);

const getColumns = (allowedColumns, options, fontSize) => {
  const columns = options.columns
    .map(col => {
      const c = allowedColumns.find(available => available.id === col.id);
      return c ? c.set("width", scalePixelsToFontSize(col.width, fontSize)) : null;
    })
    .filter(col => col);
  let left = 0;
  return columns.map((col, index) => {
    const result = col.merge({ left, index });
    left += col.width;
    return result;
  });
};

const setCellDates = (cell, item, itemType) => {
  cell.startDate = item.preferredStartDate;
  cell.latestStartDate = item.latestStartDate;
  cell.earliestEndDate = item.earliestEndDate;
  cell.endDate = item.preferredEndDate;
  cell.duration = item.preferredDuration;
  cell.durationToEarliestEnd = item.durationToEarliestEnd;
  cell.ongoing = item.ongoing;
  cell.startLabel = itemType.startDisplayLabel;
  cell.endLabel = itemType.endDisplayLabel;
};

/**
 * This function is purely an optimisation step: anything that relies on itemsById, relationshipsById, or
 * dependenciesById is calculated here and returned as a JSON string.
 * This allows us to avoid creating a new RowRecord for every item on every minor change, as when the item
 * is unaffected (i.e. when the string matches), the cached value can be returned by `getIndividualTableRow`
 * @param {ItemRecord} item
 * @param {Map} columns
 * @param {Map} itemsById
 * @param {Map} typesById
 * @param {Map} colorsById
 * @param {Map} referencesById
 * @param {Map} relationshipsById
 * @param {Map} dependenciesById
 * @param {Map} globalItemIndexes
 */
const getItemReferencesAsJsonString = (
  item,
  columns,
  itemsById,
  typesById,
  colorsById,
  referencesById,
  relationshipsById,
  dependenciesById,
  globalItemIndexes,
  fontSize,
  widthMap
) => {
  // Get relevant relationships for row.
  const itemType = typesById.get(item.type);
  const columnsMap = listToMap(columns);

  const sortByGlobalOrder = (a, b) => {
    return globalItemIndexes.get(a.id) - globalItemIndexes.get(b.id);
  };

  const itemRefs = columnsMap
    .map(column => {
      if (column.id === outlineColumnIds.blocks) {
        return item.blockingDependencies
          .map(depId => {
            const dep = dependenciesById.get(depId);
            if (!dep) {
              return null;
            }
            const depItem = itemsById.get(dep.object);
            return displayCacheSelectors.getIdentifierDisplayForItem(
              depItem,
              typesById,
              colorsById,
              fontSize,
              widthMap
            );
          })
          .filter(dep => dep)
          .sort(sortByGlobalOrder);
      } else if (column.id === outlineColumnIds.blockedBy) {
        return item.blockedByDependencies
          .map(depId => {
            const dep = dependenciesById.get(depId);
            if (!dep) {
              return null;
            }
            const depItem = itemsById.get(dep.subject);
            return displayCacheSelectors.getIdentifierDisplayForItem(
              depItem,
              typesById,
              colorsById,
              fontSize,
              widthMap
            );
          })
          .filter(dep => dep)
          .sort(sortByGlobalOrder);
      }

      if (referencesById.has(column.id)) {
        const reference = referencesById.get(column.id);
        if (itemType.references.contains(reference.id)) {
          // TODO: Need to handle bi-directional relationships
          // Find all matching relationships...
          const relationships = item.references
            .map(relId => relationshipsById.get(relId))
            .filter(rel => rel.reference === reference.id);

          if (column.cellType === TableCellTypes.singleReference) {
            const first = relationships.first();
            return first ? first.object : null;
          } else {
            return relationships
              .map(rel => {
                const relItem = itemsById.get(rel.object);
                return displayCacheSelectors.getIdentifierDisplayForItem(
                  relItem,
                  typesById,
                  colorsById,
                  fontSize,
                  widthMap
                );
              })
              .sort(sortByGlobalOrder);
          }
        }
      }
      // Default: return nothing
      return null;
    })
    .filter(ref => ref);
  return JSON.stringify(itemRefs.toJS());
};

const shouldDateCellDisable = (displayItem, itemType) => displayItem.isDateLocked || !itemType.canHaveDate;

// Old function used to calculating with minWidth and shrinkMinWidth
// See https://aeontimeline.atlassian.net/browse/ATR-1927
// eslint-disable-next-line no-unused-vars
const getRefCellHeightAndRemainingOld = (listOfSizes, maxWidth, minWidth, shrinkMinWidth) => {
  let rows = 0;
  let remaining = 0;
  let index = 0;
  let rowHeight = 0;
  let rowWidth = 0;

  const getRowHeight = i => {
    const height = listOfSizes.get(i).height;
    return Math.max(height, rowHeight);
  };

  if (listOfSizes.size > 0) {
    while (rows < TABLE_CELL_IDENTIFIERS_MAX_ROWS && index < listOfSizes.size) {
      rowHeight = getRowHeight(index);
      const size = listOfSizes.get(index);
      const width = Math.min(Math.max(size.width, minWidth), shrinkMinWidth);
      if (rowWidth + width > maxWidth) {
        // The item width is already wider than max
        if (rowWidth > 0) {
          rowWidth = width;
        }
        rows++;
      } else {
        rowWidth += width;
      }
      index++;
    }
    // In case the first item is wider than max,
    // The rows has been increased.
    rows += rowWidth > 0 ? 1 : 0;
    remaining = rows <= TABLE_CELL_IDENTIFIERS_MAX_ROWS ? 0 : listOfSizes.size - index + 1;
    rows = Math.min(rows, TABLE_CELL_IDENTIFIERS_MAX_ROWS);
  }

  return { rows, height: rows * rowHeight, remaining };
};

// Old calculating function used to limit width and number of identifiers to show
// See https://aeontimeline.atlassian.net/browse/ATR-1927
// eslint-disable-next-line no-unused-vars
const getRefCellSizeOld = (refList, column, fontSize) => {
  const maxWidth = column.width - COLUMN_BORDER_WIDTH - getIdentifierListDownArrowWidth(fontSize);
  const idFontSize = scalePixelsToFontSize(Dimensions.IDENTIFIER.FONT_SIZE, fontSize, true);
  const scaledIdentifierShrinkMinWidth = scalePixelsToFontSize(
    IDENTIFIER_SHRINK_MIN_WIDTH + TABLE_IDENTIFIER_SLUG_MARGIN_X + Dimensions.IDENTIFIER.PADDING.LEFT * 2,
    idFontSize
  );
  const scaledIdentifierMinWidth = scalePixelsToFontSize(
    TABLE_IDENTIFIER_SLUG_MIN_WIDTH + TABLE_IDENTIFIER_SLUG_MARGIN_X + Dimensions.IDENTIFIER.PADDING.LEFT * 2,
    idFontSize
  );

  let height = 0;
  let remaining = 0;
  let listToShow = refList;
  if (refList.size > 0) {
    let listOfSizes = null;
    // Maximum numbers
    remaining = Math.max(listToShow.size - TABLE_CELL_MAX_IDENTIFIERS, 0);
    listToShow = listToShow.slice(0, TABLE_CELL_MAX_IDENTIFIERS);

    listOfSizes = listToShow.map(ref => ref.tableCellSize);

    // Have fitting ones
    if (listOfSizes.size > 0) {
      const calcResult = getRefCellHeightAndRemainingOld(
        listOfSizes,
        maxWidth,
        scaledIdentifierMinWidth,
        scaledIdentifierShrinkMinWidth
      );
      remaining += calcResult.remaining;
      listToShow = listToShow.skipLast(calcResult.remaining);
      height = calcResult.height;
    }
  }
  return { size: SizeRecord({ width: maxWidth, height }), remaining, refList: listToShow };
};

const getIdentifierListDownArrowWidth = fontSize => {
  const SCALED_DOWN_ARROW_WIDTH = scalePixelsToFontSize(IDENTIFIER_LIST_DOWN_ARROW_SIZE, fontSize);
  const SCALED_DOWN_ARROW_MARGIN_X = scalePixelsToFontSize(IDENTIFIER_LIST_DOWN_ARROW_MARGIN_X, fontSize);
  return SCALED_DOWN_ARROW_WIDTH + SCALED_DOWN_ARROW_MARGIN_X * 2;
};

const COLUMN_BORDER_WIDTH = 1;

const getRefIdentifiersCellHeight = (listOfSizes, maxWidth, minWidth) => {
  let rows = 0;
  let index = 0;
  let rowHeight = 0;
  let rowWidth = 0;

  const getRowHeight = i => {
    const height = listOfSizes.get(i).height;
    return Math.max(height, rowHeight);
  };

  if (listOfSizes.size > 0) {
    while (index < listOfSizes.size) {
      rowHeight = getRowHeight(index);
      const size = listOfSizes.get(index);
      const width = Math.max(size.width, minWidth);
      if (rowWidth + width > maxWidth) {
        // The item width is already wider than max
        if (rowWidth > 0) {
          rowWidth = width;
        }
        rows++;
      } else {
        rowWidth += width;
      }
      index++;
    }
    // In case the first item is wider than max,
    // The rows has been increased.
    rows += rowWidth > 0 ? 1 : 0;
  }

  const height = rows * rowHeight;

  return { rows, height };
};

const getRefCellIdentifiersSize = (refList, maxWidth, fontSize) => {
  const idFontSize = scalePixelsToFontSize(Dimensions.IDENTIFIER.FONT_SIZE, fontSize, true);
  const scaledIdentifierMinWidth = scalePixelsToFontSize(
    TABLE_IDENTIFIER_SLUG_MIN_WIDTH + TABLE_IDENTIFIER_SLUG_MARGIN_X + Dimensions.IDENTIFIER.PADDING.LEFT * 2,
    idFontSize
  );

  let height = 0;
  let remaining = 0;
  let listToShow = refList;
  if (refList.size > 0) {
    let listOfSizes = null;
    listOfSizes = listToShow.map(ref => ref.tableCellSize);

    // Have fitting ones
    if (listOfSizes.size > 0) {
      const calcResult = getRefIdentifiersCellHeight(listOfSizes, maxWidth, scaledIdentifierMinWidth);
      height = calcResult.height;
    }
  }
  return { size: SizeRecord({ width: maxWidth, height }), remaining, refList: listToShow };
};

const getRefCellAvatarsSize = (refList, maxWidth) => {
  let remaining = 0;
  let listToShow = refList;
  let height = 0;
  let rowWidth = 0;
  if (refList.size > 0) {
    height = refList.get(0).tableCellSize.height;
    refList.forEach(r => {
      const cellWidth = r.tableCellSize.width;
      const cellHeight = r.tableCellSize.height;
      if (rowWidth + cellWidth > maxWidth) {
        rowWidth = cellWidth;
        height += cellHeight;
      } else {
        rowWidth += cellWidth;
      }
    });
  }
  return { size: SizeRecord({ width: maxWidth, height }), remaining, refList: listToShow };
};

const getRefCellSize = (refList, column, fontSize) => {
  const maxWidth = column.width - COLUMN_BORDER_WIDTH - getIdentifierListDownArrowWidth(fontSize);
  if (refList.size > 0 && refList.get(0).isAvatar) {
    return getRefCellAvatarsSize(refList, maxWidth);
  } else {
    return getRefCellIdentifiersSize(refList, maxWidth, fontSize);
  }
};

const getCellSizeOfTextProperty = (property, cellValue, column, fontSize, widthMap) => {
  let width = column.width;
  let height = OUTLINE_CELL_MIN_HEIGHT;
  if (property.format === PropertyFormatIdentifier.multitext) {
    const maxWidth = width - TABLE_CELL_TEXT_AREA_PADDING_HORIZONTAL * 2;
    const textSize = calcTextSize(cellValue, fontSize, widthMap, maxWidth).size;
    height = textSize.height + TABLE_CELL_TEXT_AREA_PADDING_VERTICAL * 2;
  }
  return { width, height };
};

const getTagCellSize = (tagsArray, column, fontSize, widthMap) => {
  const columnWidth = column.width;
  const initialWidth = TAG_CELL_MARGIN_LEFT;
  const crossHeight = scalePixelsToFontSize(TAG_CROSS_HEIGHT, fontSize);
  const tagHeight = crossHeight + TAG_PADDING_TOP + TAG_PADDING_BOTTOM + TAG_MARGIN_BOTTOM;

  let width = initialWidth;
  let height = TAG_CELL_MARGIN_TOP + tagHeight;
  const crossWidth = scalePixelsToFontSize(TAG_CROSS_WIDTH, fontSize);
  const tagPaddings = TAG_PADDING_LEFT + TAG_PADDING_RIGHT + TAG_CROSS_MARGIN_LEFT + TAG_MARGIN_RIGHT + crossWidth;
  // The +1 is for the input offset
  let placeholderWidth = 0;
  let array = tagsArray;
  //#!if readonly !== true
// placeholderWidth = TAGS_INPUT_WIDTH;
// array = tagsArray.concat([TAGS_INPUT_PLACEHOLDER]);
  //#!endif
  // calcSingleLineTextSize(TAGS_INPUT_PLACEHOLDER, fontSize, widthMap).size.width + 1;

  array.forEach(t => {
    let currentWidth = 0;
    if (t === TAGS_INPUT_PLACEHOLDER) {
      currentWidth = placeholderWidth;
    } else {
      const textWidth = calcSingleLineTextSize(t.name, fontSize, widthMap).size.width;
      currentWidth = textWidth + tagPaddings;
    }

    if (width > initialWidth && width + currentWidth > columnWidth) {
      // Next line
      height += tagHeight;
      width = initialWidth + currentWidth;
    } else {
      width += currentWidth;
    }
  });

  height = Math.max(height, OUTLINE_CELL_MIN_HEIGHT);

  return { width: columnWidth, height };
};

/**
 * Returns the cell record for the given row and column using the data provided
 * @param {number} rowIndex
 * @param {number} columnIndex
 * @param {ItemDisplayRecord} displayItem
 * @param {ItemRecord} origItem
 * @param {TableColumnRecord} column
 * @param {string} itemReferencesAsString
 * @param {string} itemType
 * @param {Map} propertiesById
 * @param {CalendarRecord} calendar
 */
const getCellForItemAndColumn = (
  rowIndex,
  columnIndex,
  displayItem,
  origItem,
  column,
  itemReferencesAsString,
  itemType,
  propertiesById,
  calendar,
  showTypeIcon,
  tagsById,
  fontSize,
  widthMap
) => {
  let cell = {
    itemId: displayItem.id,
    rowId: displayItem.id,
    columnId: column.id,
    row: rowIndex,
    column: columnIndex
  };
  let matchFound = true;
  const itemRefsJson = JSON.parse(itemReferencesAsString);
  // If the value can be found in our JSON string of precalculated item references, use the value
  // directly from that string.
  if (_.has(itemRefsJson, column.id)) {
    if (column.cellType === TableCellTypes.singleReference) {
      cell.value = itemRefsJson[column.id];
    } else {
      cell.value = List(itemRefsJson[column.id]).map(ref => immutableRecords.IdentifierDisplayRecord(ref));
    }
    if (column.id !== outlineColumnIds.blocks && column.id !== outlineColumnIds.blockedBy) {
      if (!itemType.references.contains(column.id)) {
        cell.disabled = true;
      }
    }
    // Get the calculated size
    const calcResult = getRefCellSize(cell.value, column, fontSize);
    cell.size = calcResult.size;
    matchFound = true;
  } else {
    const dateCellDisabled = shouldDateCellDisable(displayItem, itemType);
    switch (column.id) {
      case outlineColumnIds.label:
        cell.value = displayItem.label;
        cell.placeholder = "Untitled";
        cell.isStruckOut = displayItem.isCompleted;
        if (showTypeIcon) {
          cell.icon = itemType.icon;
          cell.iconColor = itemType.color;
        }
        break;
      case outlineColumnIds.identifier:
        cell.value = typeSelectors.isAvatarType(itemType)
          ? itemSelectors.getInitialsForItem(displayItem)
          : displayItem.displayId;
        cell.color = displayItem.color;
        cell.image = displayItem.image;
        cell.isAvatar = typeSelectors.isAvatarType(itemType);
        cell.size = displayItem.identifierSize;
        cell.disabled = true;
        break;
      case outlineColumnIds.sequence:
        cell.value = displayItem.nestedIndex;
        break;
      case outlineColumnIds.narrative:
        cell.value = displayItem.narrativeTextSize.text;
        break;
      case outlineColumnIds.summary:
        cell.value = displayItem.summary;
        break;
      case outlineColumnIds.tag: {
        const value = displayItem.tags.map(tId => ({ id: tId, name: tagsById.get(tId) })).toJS();
        cell.value = value;
        const size = getTagCellSize(value, column, fontSize, widthMap);
        cell.size = size;
        break;
      }
      case outlineColumnIds.type:
        cell.value = displayItem.type;
        break;
      case outlineColumnIds.color: {
        cell.value = origItem.color;
        break;
      }
      case outlineColumnIds.ongoing:
        cell.value = displayItem.ongoing;
        cell.disabled = dateCellDisabled;
        break;
      case outlineColumnIds.start:
        cell.value = displayItem.preferredStartDate;
        cell.disabled = dateCellDisabled;
        setCellDates(cell, displayItem, itemType);
        cell.isWarning = utils.arePreferredStartDifferentFromActualStart(displayItem);
        if (cell.isWarning) {
          cell.warningMessage = interfaceMessages.TABLE_VIEW_CHILDREN_DATES_ARE_OUTSIDE_RANGE;
        }
        break;
      case outlineColumnIds.latestStart:
        cell.value = displayItem.latestStartDate;
        cell.disabled = dateCellDisabled;
        setCellDates(cell, displayItem, itemType);
        break;
      case outlineColumnIds.duration:
        cell.value = displayItem.preferredDuration;
        cell.disabled =
          dateCellDisabled ||
          displayItem.ongoing ||
          isDateValid(displayItem.latestStartDate) ||
          isDateValid(displayItem.earliestEndDate);
        break;
      case outlineColumnIds.earliestEnd:
        cell.value = displayItem.earliestEndDate;
        cell.disabled = dateCellDisabled || displayItem.ongoing;
        setCellDates(cell, displayItem, itemType);
        break;
      case outlineColumnIds.end:
        cell.value = displayItem.preferredEndDate;
        cell.disabled = dateCellDisabled || displayItem.ongoing;
        setCellDates(cell, displayItem, itemType);
        cell.isWarning = utils.arePreferredEndDifferentFromActualEnd(displayItem);
        if (cell.isWarning) {
          cell.warningMessage = interfaceMessages.TABLE_VIEW_CHILDREN_DATES_ARE_OUTSIDE_RANGE;
        }
        break;
      case outlineColumnIds.childRange: {
        let value = "";
        if (utils.arePreferredDatesDifferentFromActualDates(displayItem)) {
          const { startStr, endStr, durationStr } = utils.getActualDateStrings(calendar, displayItem);
          value = `${startStr} - ${endStr} (${durationStr})`;
        }
        cell.value = value;
        cell.disabled = true;
        break;
      }
      default:
        matchFound = false;
        break;
    }

    if (!matchFound) {
      // Wasn't a default value or an allowed reference, so check to see if it is a property instead
      const property = propertiesById.get(column.id);
      matchFound = Boolean(property);
      if (property) {
        if (itemType.properties.contains(property.id)) {
          const cellValue = origItem.propertyValues.get(property.id);
          cell.size = getCellSizeOfTextProperty(property, cellValue, column, fontSize, widthMap);
          cell.value = cellValue;
          cell.disabled = property.autoCalculate && displayItem.children.size > 0;
        } else {
          cell.disabled = true;
        }
      }
    }

    if (!matchFound) {
      // If we reach here, we did not match anything for the cell.
      // That means it is likely to be a reference that this item type is not allowed.
      cell.disabled = true;
    }
  }

  return immutableRecords.TableCellRecord(cell);
};

const getEmptyCellForColumn = (rowIndex, columnIndex, column, defaultTypeLabel) =>
  immutableRecords.TableCellRecord({
    itemId: null,
    columnId: column.id,
    row: rowIndex,
    rowId: TableRowTypes.EMPTY,
    column: columnIndex,
    //#!if readonly === true
    disabled: true,
    placeholder: null,
    //#!else
    /* eslint-disable no-dupe-keys */
/* disabled: column.id !== outlineColumnIds.label,*/
/* placeholder:*/
/* column.id === outlineColumnIds.label*/
/* ? interfaceMessages.TABLE_VIEW_TYPE_TO_CREATE + (defaultTypeLabel ? defaultTypeLabel.toLowerCase() : "item")*/
/* : null*/
    //#!endif
  });

/**
 * Generates an empty roqw
 * @param {string} rowIndex
 * @param {string} columns
 * @param {string} viewId
 */
const getEmptyRow = createCachedSelector(
  // inputs,
  (rowIndex, _columns, _top, _viewId) => rowIndex,
  (rowIndex, columns, _top, _viewId) => columns,
  (rowIndex, columns, top, _viewId) => top,
  (_rowIndex, _columns, _top, _viewId, fontSize) => fontSize,
  (_rowIndex, _columns, _top, _viewId, _fontSize, defaultTypeLabel) => defaultTypeLabel,
  // output
  (rowIndex, columns, top, fontSize, defaultTypeLabel) => {
    const height = Math.max(fontSize * 1.3 + 2 * OUTLINE_CELL_PADDING, OUTLINE_CELL_MIN_HEIGHT);
    // Now let's create an "empty row" to be appended at the bottom of the table.
    // Typing into this row will create a new item.
    const emptyCells = columns.map((col, colIndex) => getEmptyCellForColumn(rowIndex, colIndex, col, defaultTypeLabel));
    return immutableRecords.TableRowRecord({
      id: TableRowTypes.EMPTY,
      parentRowId: null,
      type: null,
      cells: emptyCells,
      index: rowIndex,
      top,
      height: height,
      indentationLevel: 0,
      hasChildren: false,
      childrenExpanded: false
    });
  }
)((rowIndex, columns, top, viewId) => viewId);

const getRowHeight = (row, cells, summaryColumnShown, titleMetricsKey) => {
  const titleMetrics = row.get(titleMetricsKey);
  const titleSize = titleMetrics.size;
  let height;
  if (summaryColumnShown) {
    const summarySize = row.summaryMetrics.size;
    height = Math.max(
      Math.max(titleSize.height, summarySize.height) + 2 * OUTLINE_CELL_PADDING,
      OUTLINE_CELL_MIN_HEIGHT
    );
  } else {
    height = Math.max(titleSize.height + 2 * OUTLINE_CELL_PADDING, OUTLINE_CELL_MIN_HEIGHT);
  }
  height = cells.reduce((max, c) => (height = Math.max(c.size.height, height)), height);
  return height;
};

/**
 * @param {string} viewId (0)
 * @param {ItemRecord} row (1)
 * @param {number} rowIndex (2)
 * @param {Map} columns (3)
 * @param {Map} itemsById (4)
 * @param {Map} typesById (5)
 * @param {Map} colorsById (6)
 * @param {Map} referencesById (7)
 * @param {Map} propertiesById (8)
 * @param {Map} relationshipsById (9)
 * @param {Map} dependenciesById (10)
 * @param {List} expandedItems (11)
 * @param {string} titleMetricsKey (12)
 * @param {bool} summaryColumnShown (13)
 * @param {CalendarRecord} calendar (14)
 * @param {CalendarRecord} violatedDateConstraints (15)
 * @param {CalendarRecord} violatedDependencies (16)
 * @param {CalendarRecord} showTypeIcon (17)
 * @param {Map} globalItemIndexes (18)
 * @param {number} fontSize (19)
 * @param {Map} widthMap (20)
 * @param {Map} tagsById (21)
 */
// May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// premature optimisation.
const getIndividualTableRow = createCachedSelector(
  // Use these inputs directly
  (...params) => params[1], // row (displayItem)
  (...params) => params[2], // rowIndex
  (...params) => params[3], // columns
  (...params) => params[4].get(params[1].id), // item (itemsById.get(row.id)
  (...params) => params[5].get(params[1].type), // itemType (typesById.get(displayItem.type)
  (...params) => params[8], // propertiesById
  (...params) => params[11].contains(params[1].id), // childrenExpanded
  (...params) => params[12], // titleMetricsKey
  (...params) => params[13], // summaryColumnShown
  (...params) => params[14], // calendar
  (...params) =>
    getItemReferencesAsJsonString(
      // calculate json string for referenced items
      params[1], // item
      params[3], // column
      params[4], // itemsById
      params[5], // typesById
      params[6], // colorsById
      params[7], // referencesById
      params[9], // relationshipsById
      params[10], // dependenciesById
      params[18], // globalItemIndexes
      params[19],
      params[20]
    ),
  (...params) => params[15], // violatedDateConstraints
  (...params) => params[16], // violatedDependencies
  (...params) => params[17], // showTypeIcon
  (...params) => params[21], // tagsById
  (...params) => params[19], // fontSize
  (...params) => params[20],
  // Final Output Calculation
  (
    row,
    rowIndex,
    columns,
    item,
    itemType,
    propertiesById,
    childrenExpanded,
    titleMetricsKey,
    summaryColumnShown,
    calendar,
    itemReferencesAsString,
    violatedDateConstraints,
    violatedDependencies,
    showTypeIcon,
    tagsById,
    fontSize,
    widthMap
  ) => {
    // Get relevant relationships for row.
    const cells = columns.map((col, colIndex) =>
      getCellForItemAndColumn(
        rowIndex,
        colIndex,
        row,
        item,
        col,
        itemReferencesAsString,
        itemType,
        propertiesById,
        calendar,
        showTypeIcon,
        tagsById,
        fontSize,
        widthMap
      )
    );

    const hasViolatedDateConstraints = violatedDateConstraints.find(c => c.subject === item.id || c.object === item.id);
    const hasViolatedDependencies = violatedDependencies.find(c => c.subject === item.id || c.object === item.id);

    const height = getRowHeight(row, cells, summaryColumnShown, titleMetricsKey);

    const rowRecord = immutableRecords.TableRowRecord({
      id: row.id,
      parentRowId: row.super, // Note: may have been overridden in Narrative sorting to be the narrative parent
      type: row.type,
      cells,
      index: rowIndex,
      height,
      indentationLevel: row.superDepth, // Note: may have been overridden in Narrative sorting to be the narrative indentation
      hasChildren: row.children.size > 0,
      childrenExpanded,
      isFiltered: row.isFiltered,
      hasViolatedConstraints: Boolean(hasViolatedDateConstraints || hasViolatedDependencies)
    });
    return rowRecord;
  }
)((viewId, row) => `${viewId}-${row.id}`);

/**
 * @param {string} viewId
 * @param {TableRowRecord} rowRecord
 * @param {top} top
 */
// May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
// premature optimisation.
const getIndividualRowWithTop = createCachedSelector(
  // Use these inputs directly
  (_viewId, rowRecord, _top) => rowRecord,
  (_viewId, rowRecord, top) => top,
  // Final Output Calculation
  (rowRecord, top) => {
    return rowRecord.set("top", top);
  }
)((viewId, row) => `${viewId}-${row.id}`);

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {List} rows
 * @param {List} columns
 * @param {string} titleMetricsKey
 */
const getTableData = createCachedSelector(
  // fetch parameters for last calculate function
  (state, viewId) => viewId,
  state => state.data.items.byId,
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getEnabledDefinitions(state).colors.byId,
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getPropertiesById(state),
  state => state.data.relationships.byId,
  state => state.data.dependencies.byId,
  (state, viewId, rows) => rows,
  (state, viewId, rows, columns) => columns,
  appStateSelectors.getViewExpandedItems,
  appStateSelectors.getFontSize,
  characterWidthMapSelectors.getCharacterWidthMap,
  (state, viewId, rows, columns, titleMetricsKey) => titleMetricsKey,
  state => calendarSelectors.getCalendar(state),
  state => displayCacheSelectors.getViolatedDateConstraints(state),
  state => displayCacheSelectors.getViolatedDependencies(state),
  (state, viewId, rows, columns, _titleMetricsKey, showTypeIcon) => showTypeIcon,
  state => itemSelectors.getItemIndexesInTreeOrderForAllTypes(state),
  (state, _viewId, _rows, _columns, _titleMetricsKey, _showTypeIcon, useNarrativeType) =>
    useNarrativeType
      ? definitionSelectors.getDefaultNarrativeDocumentType(state)
      : definitionSelectors.getDefaultSequenceType(state),
  tagSelectors.getTags,
  // Calculate result
  (
    viewId,
    itemsById,
    typesById,
    colorsById,
    referencesById,
    propertiesById,
    relationshipsById,
    dependenciesById,
    rows,
    columns,
    expandedItems,
    fontSize,
    widthMap,
    titleMetricsKey,
    calendar,
    violatedDateConstraints,
    violatedDependencies,
    showTypeIcon,
    globalItemIndexes,
    defaultTypeId,
    tagsById
  ) => {
    const totalGroupWidth = columns.reduce((sum, g) => (sum += g.width), 0);
    const width =
      totalGroupWidth +
      Dimensions.SCROLLBAR_TRACK_SIZE +
      scalePixelsToFontSize(OUTLINE_CELL_ROW_GUTTER_WIDTH, fontSize);

    const HEADER_HEIGHT = scalePixelsToFontSize(OUTLINE_HEADER_HEIGHT, fontSize);

    const summaryColumnShown = columns.find(c => c.id === outlineColumnIds.summary);

    let top = 0;

    const mergedRows = rows.map((row, rowIndex) => {
      // May choose to switch this to list selector if it is slow enough, but suspect it is a fast function so avoiding
      // premature optimisation.
      let rowRecord = getIndividualTableRow(
        viewId,
        row,
        rowIndex,
        columns,
        itemsById,
        typesById,
        colorsById,
        referencesById,
        propertiesById,
        relationshipsById,
        dependenciesById,
        expandedItems,
        titleMetricsKey,
        summaryColumnShown,
        calendar,
        violatedDateConstraints,
        violatedDependencies,
        showTypeIcon,
        globalItemIndexes,
        fontSize,
        widthMap,
        tagsById
      );
      rowRecord = getIndividualRowWithTop(viewId, rowRecord, top);
      top += rowRecord.height;
      return rowRecord;
    });
    // Now let's create an "empty row" to be appended at the bottom of the table.
    // Typing into this row will create a new item.
    const defaultTypeLabel = typesById.get(defaultTypeId).label;
    const emptyRow = wrapEditingComponent(getEmptyRow(rows.size, columns, top, viewId, fontSize, defaultTypeLabel));
    top += OUTLINE_CELL_MIN_HEIGHT;
    top += HEADER_HEIGHT + Dimensions.SCROLLBAR_TRACK_SIZE;

    return immutableRecords.TableRecord({
      rows: mergedRows.push(emptyRow).filter(r => r),
      columns,
      width: width,
      height: top,
      headerHeight: HEADER_HEIGHT,
      titleMetricsKey
    });
  }
)(workViewSelectors.getViewId);

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {List} rows
 */
const getSortedRowIds = createCachedSelector(
  // fetch parameters for last calculate function
  (state, viewId, rows) => rows,
  // Calculate result
  rows => {
    return rows.map(r => r.id);
  }
)(workViewSelectors.getViewId);

const getTagActionForValueChange = (itemIds, tagNames, isAdding) => {
  return isAdding ? tagOperations.addTags(itemIds, tagNames) : tagOperations.removeTags(itemIds, tagNames);
};

/**
 *
 * @param {string} itemId
 * @param {string} columnId
 * @param {*} value
 * @param {TimelineRecord} globalState
 * @param {boolean} newComponentState
 * @param {boolean} removeOldValues
 */
const createActionForValueChange = (
  itemId,
  columnId,
  value,
  globalState,
  newComponentState = null,
  removeOldValues = false
) => {
  switch (columnId) {
    case outlineColumnIds.label:
      return itemOperations.editLabel([itemId], value);
    case outlineColumnIds.summary:
      return itemOperations.editSummary([itemId], value);
    case outlineColumnIds.tag:
      return getTagActionForValueChange([itemId], value.tagNames, value.isAdding);
    case outlineColumnIds.type: {
      // Check any breaks and show confirmation
      const displayItemsById = displayCacheSelectors.getDisplayItemsById(globalState);
      const itemsById = itemSelectors.getItemsById(globalState);
      const typesById = typeSelectors.getTypesById(globalState);
      const referencesById = referenceSelectors.getReferencesById(globalState);
      const relationshipsById = relationshipSelectors.getRelationshipsById(globalState);
      const displayItem = displayItemsById.get(itemId);
      // Note: displayItem is not existed means that item is new created from
      // the bulk changes including this one
      // The type is determined in the action of creating the item,
      // no need to do that here
      if (
        displayItem &&
        displayItem.type !== value &&
        (!typeSelectors.doesNewTypeBreakItemValue(
          value,
          [displayItem],
          itemsById,
          typesById,
          referencesById,
          relationshipsById
        ) ||
          dialogs.UNSAFE_SYNCHRONOUS_confirmationPrompt(interfaceMessages.TYPE_CHANGE_CONFIRMATION))
      ) {
        return itemOperations.changeType([itemId], value);
      }
      return;
    }
    case outlineColumnIds.color:
      return itemOperations.editColor([itemId], value);
    case outlineColumnIds.ongoing:
      return itemOperations.setOngoing([itemId], value);
    case outlineColumnIds.start:
      return itemOperations.setPreferredStartDate(
        [itemId],
        value ? value.timestamp : null,
        value ? value.precision : null
      );
    case outlineColumnIds.latestStart:
      return itemOperations.setLatestStartDate(
        [itemId],
        value ? value.timestamp : null,
        value ? value.precision : null
      );
    case outlineColumnIds.duration:
      return itemOperations.setPreferredDuration([itemId], value ? value.toObject() : { seconds: 0 });
    case outlineColumnIds.earliestEnd:
      return itemOperations.setEarliestEndDate(
        [itemId],
        value ? value.timestamp : null,
        value ? value.precision : null
      );
    case outlineColumnIds.end:
      return itemOperations.setPreferredEndDate(
        [itemId],
        value ? value.timestamp : null,
        value ? value.precision : null
      );
    case OUTLINE_COLUMN_BULK_CHANGE_DATES:
      return itemOperations.setBulkPreferredDates([itemId], value);
    case outlineColumnIds.blocks: {
      if (removeOldValues) {
        const newDependencies = value
          ? value
            .map(val =>
              val ? dependencyOperations.createDependencies([itemId], [val], DependencyTypes.FS, [itemId]) : null
            )
            .filter(r => r)
            .toArray()
          : [];

        return [dependencyOperations.removeDependenciesWithSubjects([itemId]), ...newDependencies];
      }
      if (newComponentState) {
        return dependencyOperations.createDependencies([itemId], [value], DependencyTypes.FS, [itemId]);
      } else {
        return dependencyOperations.removeDependenciesWithValues([itemId], [value], [itemId]);
      }
    }
    case outlineColumnIds.blockedBy: {
      if (removeOldValues) {
        const newDependencies = value
          ? value
            .map(val =>
              val ? dependencyOperations.createDependencies([val], [itemId], DependencyTypes.FS, [itemId]) : null
            )
            .filter(r => r)
            .toArray()
          : [];

        return [dependencyOperations.removeDependenciesWithObjects([itemId]), ...newDependencies];
      }
      if (newComponentState) {
        return dependencyOperations.createDependencies([value], [itemId], DependencyTypes.FS, [itemId]);
      } else {
        return dependencyOperations.removeDependenciesWithValues([value], [itemId], [itemId]);
      }
    }
    default:
      break;
  }

  // This is not ideal, and may need to be cleaned up later if it is no longer valid...
  // However, newState is only passed along for relationships and not for properties
  // So we can use that to differentiate between properties and relationship IDs

  // If it does not match above, it is a property value.
  if (_.isNil(newComponentState)) {
    // No newState passed in, so this must be a property (given what is left that it could be)
    return itemOperations.setPropertyValue([itemId], columnId, value);
  } else {
    // newState passed in, so this must be a reference)
    if (value) {
      if (removeOldValues) {
        const newRelationships = value
          ? value
            .map(val => (val ? relationshipOperations.createRelationship([itemId], columnId, val) : null))
            .filter(r => r)
            .toArray()
          : [];
        return [relationshipOperations.deleteRelationshipsWithValues([itemId], columnId, null), ...newRelationships];
      } else if (newComponentState) {
        return relationshipOperations.createRelationship([itemId], columnId, value);
      } else {
        return relationshipOperations.deleteRelationshipsWithValues([itemId], columnId, value);
      }
    } else {
      return relationshipOperations.deleteRelationshipsWithValues([itemId], columnId, null);
    }
  }
};

const getItemTypeForBulkChanges = (newId, bulkChanges, defaultType) => {
  const typeChange = bulkChanges.findLast(c => c.rowId === newId && c.columnId === outlineColumnIds.type);
  return typeChange ? typeChange.value : defaultType;
};

const getChangeValueForBulkChange = change => {
  let value = change.value;
  if (change.columnId === outlineColumnIds.tag) {
    value = { tagNames: value, isAdding: Boolean(value) };
  }
  return value;
};

const createActionForBulkChange = (newRowIds, bulkChanges, defaultType, globalState) => {
  const createActions = newRowIds
    .map(newId => {
      const type = getItemTypeForBulkChanges(newId, bulkChanges, defaultType);
      return itemOperations.createItemWithId(newId, type);
    })
    .toArray();
  const changeActions = _.flatten(
    bulkChanges
      .map(change => {
        return createActionForValueChange(
          change.rowId,
          change.columnId,
          getChangeValueForBulkChange(change),
          globalState,
          change.newState,
          true
        );
      })
      .toArray()
  );
  return batchActions(_.concat(createActions, changeActions));
};

export default {
  getAllowedColumns,
  getAvailableColumns,
  getColumns,
  getOptionsForColumn,
  getTableData,
  getSortedRowIds,
  getCellForItemAndColumn,
  getIndividualTableRow,
  tableCellTypeForProperty,

  // Actions to handle edits within table cells
  createActionForValueChange,
  createActionForBulkChange
};
